import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Plus } from 'lucide-react';
import { cn } from '@/utils/cn';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import { CampaignDetailsNegativeKeywords } from '@/components/CampaignDetailsNegativeKeywords';
import { CreateSbAdGroupModal } from '@/components/campaign-create/create-adgroup/CreateSbAdGroupModal';
import { CreateSpAdGroupModal } from '@/components/campaign-create/create-adgroup/CreateSpAdGroupModal';
import { AdGroupActiveSwitch } from '@/components/AdGroupActiveSwitch';
import { CampaignPlacementsSection } from '@/components/CampaignPlacementsSection';
import { CampaignSettingsSection } from '@/components/CampaignSettingsSection';
import { TableSearchInput } from '@/components/TableSearchInput';
import { useToast } from '@/components/ui/Toast';
import {
  adGroupSectionsFor,
  CampaignAdGroupContent,
  type AdGroupSectionId,
} from '@/components/CampaignAdGroupContent';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';
import {
  canCreateSpAdGroup,
  spCampaignTargetingTypeLabel,
} from '@/utils/spAdGroupCreate';
import { canCreateSbAdGroup } from '@/utils/sbAdGroupCreate';
import { resolveAdGroupActiveSwitchMode } from '@/utils/adGroupActiveState';
import {
  AD_GROUP_CANNOT_TOGGLE_MESSAGE,
  AD_GROUP_ENABLED_MESSAGE,
  AD_GROUP_MISSING_MESSAGE,
  AD_GROUP_PAUSED_MESSAGE,
  adGroupStateApiErrorMessage,
  updateAdGroupState,
} from '@/api/adGroupStateUpdate';

export const CAMPAIGN_DETAILS_SECTIONS = [
  { id: 'ad-groups', label: 'Ad groups' },
  { id: 'placements', label: 'Placements' },
  { id: 'negative-keywords', label: 'Negative keywords' },
  { id: 'campaign-settings', label: 'Campaign settings' },
] as const;

export type CampaignDetailsSectionId = (typeof CAMPAIGN_DETAILS_SECTIONS)[number]['id'];

export function parseCampaignDetailsSection(raw: string | null): CampaignDetailsSectionId {
  if (raw === 'placements' || raw === 'negative-keywords' || raw === 'campaign-settings') {
    return raw;
  }
  // Default, and legacy `products` links (products now live under each ad group's Ads tab).
  return 'ad-groups';
}

export type CampaignDetailsAdGroupRow = {
  ad_group_id?: string;
  name?: string;
  state?: string;
  default_bid?: number | string | null;
  extended_data?: {
    serving_status?: string;
  } | null;
};

type CampaignDetailsSectionPanelProps = {
  profileId: string;
  campaignId: string;
  campaign: SpCampaignNormalized;
  adProduct: CampaignAdProduct;
  section: CampaignDetailsSectionId;
  onSectionChange: (section: CampaignDetailsSectionId) => void;
  adGroups: CampaignDetailsAdGroupRow[];
  adGroupsTotal: number;
  adGroupsLoading: boolean;
  adGroupsError: Error | null;
  onRetryAdGroups: () => void;
  adGroupNameById?: Map<string, string>;
  /** Ad group the user drilled into from the Ad groups table, if any. */
  selectedAdGroupId: string | null;
  adGroupSection: AdGroupSectionId;
  onAdGroupSectionChange: (section: AdGroupSectionId) => void;
  adGroupHref: (adGroupId: string) => string;
  onBackToAdGroups: () => void;
  /** Refresh campaign after placements or settings are saved. */
  onCampaignUpdated?: () => void;
};

const SECTION_BUTTON_BASE = 'w-full text-left px-4 py-2.5 text-sm transition-colors border-l-4';
const SECTION_BUTTON_ACTIVE =
  'border-success bg-success-tint text-success-ink font-semibold rounded-r-lg';
const SECTION_BUTTON_IDLE =
  'border-transparent text-foreground-muted hover:bg-surface-muted/80 font-normal';

export function CampaignDetailsSectionPanel({
  profileId,
  campaignId,
  campaign,
  adProduct,
  section,
  onSectionChange,
  adGroups,
  adGroupsTotal,
  adGroupsLoading,
  adGroupsError,
  onRetryAdGroups,
  adGroupNameById,
  selectedAdGroupId,
  adGroupSection,
  onAdGroupSectionChange,
  adGroupHref,
  onBackToAdGroups,
  onCampaignUpdated,
}: CampaignDetailsSectionPanelProps) {
  const qc = useQueryClient();
  const { showToast } = useToast();
  const [adGroupsSearchQuery, setAdGroupsSearchQuery] = useState('');
  const [createAdGroupOpen, setCreateAdGroupOpen] = useState(false);
  const [pendingAdGroupIds, setPendingAdGroupIds] = useState<Set<string>>(() => new Set());
  const pendingAdGroupIdsRef = useRef(pendingAdGroupIds);
  pendingAdGroupIdsRef.current = pendingAdGroupIds;

  const canCreateAdGroup = canCreateSpAdGroup(campaign) || canCreateSbAdGroup(campaign);

  const visibleAdGroups = useMemo(() => {
    return filterRowsByTableSearch(adGroups, adGroupsSearchQuery, (adGroup) => [
      adGroup.name,
      adGroup.ad_group_id,
      adGroup.state,
      adGroup.default_bid,
      adGroup.extended_data?.serving_status,
    ]);
  }, [adGroups, adGroupsSearchQuery]);

  const handleAdGroupActiveToggle = useCallback(
    async (adGroup: CampaignDetailsAdGroupRow) => {
      const adGroupId = adGroup.ad_group_id?.trim();
      if (!adGroupId) {
        showToast(AD_GROUP_MISSING_MESSAGE, 'error');
        return;
      }
      if (pendingAdGroupIdsRef.current.has(adGroupId)) return;

      const mode = resolveAdGroupActiveSwitchMode(adGroup.state);
      if (mode.kind !== 'toggle') {
        showToast(AD_GROUP_CANNOT_TOGGLE_MESSAGE, 'error');
        return;
      }

      const targetState = mode.targetState;
      setPendingAdGroupIds((prev) => {
        const next = new Set(prev);
        next.add(adGroupId);
        return next;
      });

      try {
        await updateAdGroupState({
          profileId,
          adGroupId,
          adProduct,
          state: targetState,
        });
        await Promise.all([
          qc.invalidateQueries({
            queryKey: [`${adProduct.toLowerCase()}-ad-groups`, profileId, campaignId],
          }),
          qc.invalidateQueries({ queryKey: ['sp-ad-groups', profileId] }),
          qc.invalidateQueries({ queryKey: ['sb-ad-groups', profileId] }),
          qc.invalidateQueries({ queryKey: ['sd-ad-groups', profileId] }),
        ]);
        onRetryAdGroups();
        showToast(
          targetState === 'ENABLED' ? AD_GROUP_ENABLED_MESSAGE : AD_GROUP_PAUSED_MESSAGE,
          'success'
        );
      } catch (error) {
        showToast(adGroupStateApiErrorMessage(error), 'error');
      } finally {
        setPendingAdGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(adGroupId);
          return next;
        });
      }
    },
    [adProduct, campaignId, onRetryAdGroups, profileId, qc, showToast]
  );


  const inAdGroup = section === 'ad-groups' && !!selectedAdGroupId;
  const selectedAdGroup = selectedAdGroupId
    ? adGroups.find((adGroup) => adGroup.ad_group_id === selectedAdGroupId)
    : undefined;
  const adGroupSections = adGroupSectionsFor(adProduct);
  // SB ad groups have no default bid; SD ad groups have no serving status.
  const showDefaultBid = adProduct !== 'SB';
  const showServingStatus = adProduct !== 'SD';

  return (
    <div className="bg-surface rounded-xl shadow border border-border overflow-hidden flex flex-col sm:flex-row min-h-[320px]">
      <aside className="w-full sm:w-52 shrink-0 border-b sm:border-b-0 sm:border-r border-border bg-page/80 py-3 shadow-[2px_0_8px_-4px_rgba(0,0,0,0.08)]">
        {inAdGroup ? (
          <>
            <button
              type="button"
              onClick={onBackToAdGroups}
              className="flex items-center gap-2 px-4 py-2.5 mb-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" aria-hidden />
              Back to Ad groups
            </button>
            <nav aria-label="Ad group sections" className="flex flex-col gap-0.5">
              {adGroupSections.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onAdGroupSectionChange(item.id)}
                  className={cn(
                    SECTION_BUTTON_BASE,
                    adGroupSection === item.id ? SECTION_BUTTON_ACTIVE : SECTION_BUTTON_IDLE
                  )}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </>
        ) : (
          <nav aria-label="Campaign details sections" className="flex flex-col gap-0.5">
            {CAMPAIGN_DETAILS_SECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSectionChange(item.id)}
                className={cn(
                  SECTION_BUTTON_BASE,
                  section === item.id ? SECTION_BUTTON_ACTIVE : SECTION_BUTTON_IDLE
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>
        )}
      </aside>

      <div className="flex-1 min-w-0 p-5">
        {inAdGroup && selectedAdGroupId ? (
          <CampaignAdGroupContent
            profileId={profileId}
            campaignId={campaignId}
            adGroupId={selectedAdGroupId}
            adProduct={adProduct}
            adGroupName={adGroupNameById?.get(selectedAdGroupId) || selectedAdGroupId}
            adGroupState={selectedAdGroup?.state}
            campaignBudget={campaign.budget}
            campaignTargetingLabel={spCampaignTargetingTypeLabel(campaign)}
            section={adGroupSection}
            adGroupNameById={adGroupNameById}
          />
        ) : (
        <>
        {section === 'ad-groups' && (
          <div id="ad-groups">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-lg font-semibold text-foreground">Ad groups</h2>
              <div className="flex flex-wrap items-center gap-2">
                {!adGroupsLoading && !adGroupsError && adGroups.length > 0 ? (
                  <TableSearchInput
                    value={adGroupsSearchQuery}
                    onChange={setAdGroupsSearchQuery}
                    placeholder="Search ad groups…"
                    id="campaign-details-section-ad-groups-search"
                  />
                ) : null}
                {canCreateAdGroup ? (
                  <button
                    type="button"
                    onClick={() => setCreateAdGroupOpen(true)}
                    className="text-sm px-3 py-1.5 bg-brand text-brand-foreground rounded-lg hover:bg-brand-hover font-medium inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-4 h-4" aria-hidden />
                    New ad group
                  </button>
                ) : null}
                {!adGroupsLoading && !adGroupsError && adGroups.length > 0 ? (
                  <span className="text-sm text-foreground-subtle">{adGroupsTotal} total</span>
                ) : null}
              </div>
            </div>
            {adGroupsLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-subtle">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading ad groups...
              </div>
            ) : adGroupsError ? (
              <div className="text-sm text-error">
                {adGroupsError.message || 'Failed to load ad groups'}
                <button
                  type="button"
                  onClick={() => void onRetryAdGroups()}
                  className="ml-2 text-brand underline"
                >
                  Retry
                </button>
              </div>
            ) : adGroups.length === 0 ? (
              <p className="text-sm text-foreground-subtle">
                No ad groups found for this campaign.
                {canCreateAdGroup ? ' Create one to start targeting.' : ''}
              </p>
            ) : visibleAdGroups.length === 0 ? (
              <p className="text-sm text-foreground-subtle">No rows match your search.</p>
            ) : (
              <>
                {adGroupsSearchQuery.trim() ? (
                  <p className="text-sm text-foreground-subtle mb-2">
                    Showing {visibleAdGroups.length} of {adGroups.length} ad group(s)
                  </p>
                ) : null}
                <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Ad group ID</th>
                        <th className="px-3 py-2 text-center">Active</th>
                        {showDefaultBid ? <th className="px-3 py-2">Default bid</th> : null}
                        {showServingStatus ? (
                          <th className="px-3 py-2">Serving status</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {visibleAdGroups.map((adGroup, index) => {
                        const adGroupId = adGroup.ad_group_id || `idx-${index}`;
                        const pending = Boolean(
                          adGroup.ad_group_id && pendingAdGroupIds.has(adGroup.ad_group_id)
                        );
                        return (
                          <tr key={adGroupId} className="bg-surface hover:bg-page/80">
                            <td className="px-3 py-2 text-foreground">
                              {adGroup.ad_group_id ? (
                                <Link
                                  to={adGroupHref(adGroup.ad_group_id)}
                                  className="min-w-0 truncate text-brand hover:underline"
                                  title={adGroup.name || adGroup.ad_group_id}
                                >
                                  {adGroup.name || adGroup.ad_group_id}
                                </Link>
                              ) : (
                                adGroup.name || '-'
                              )}
                            </td>
                            <td className="px-3 py-2 text-foreground-muted font-mono">
                              {adGroup.ad_group_id || '-'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <AdGroupActiveSwitch
                                adGroupName={adGroup.name || adGroup.ad_group_id || 'Ad group'}
                                state={adGroup.state}
                                pending={pending}
                                onToggle={() => {
                                  void handleAdGroupActiveToggle(adGroup);
                                }}
                              />
                            </td>
                            {showDefaultBid ? (
                              <td className="px-3 py-2 text-foreground-muted">
                                {adGroup.default_bid != null ? String(adGroup.default_bid) : '-'}
                              </td>
                            ) : null}
                            {showServingStatus ? (
                              <td className="px-3 py-2 text-foreground-muted">
                                {adGroup.extended_data?.serving_status || '-'}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {section === 'placements' && (
          <CampaignPlacementsSection
            profileId={profileId}
            campaign={campaign}
            adProduct={adProduct}
            onUpdated={onCampaignUpdated}
          />
        )}

        {section === 'negative-keywords' && (
          <CampaignDetailsNegativeKeywords
            profileId={profileId}
            campaignId={campaignId}
            adProduct={adProduct}
            adGroups={adGroups}
            adGroupsLoading={adGroupsLoading}
            adGroupNameById={adGroupNameById}
          />
        )}

        {section === 'campaign-settings' && (
          <CampaignSettingsSection
            profileId={profileId}
            campaign={campaign}
            onUpdated={onCampaignUpdated}
          />
        )}
        </>
        )}
      </div>
      {canCreateSpAdGroup(campaign) ? (
        <CreateSpAdGroupModal
          profileId={profileId}
          campaignId={campaignId}
          campaign={campaign}
          isOpen={createAdGroupOpen}
          onClose={() => setCreateAdGroupOpen(false)}
        />
      ) : null}
      {canCreateSbAdGroup(campaign) ? (
        <CreateSbAdGroupModal
          profileId={profileId}
          campaignId={campaignId}
          campaign={campaign}
          isOpen={createAdGroupOpen}
          onClose={() => setCreateAdGroupOpen(false)}
        />
      ) : null}
    </div>
  );
}
